import { Label } from "layout/typography/Label";
import { sortBy } from "lodash";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { useState } from "react";
import { useGetAllAccountsQuery } from "store/queries/account";
import { useGetPayersByAccountQuery } from "store/queries/payers";
import { useLazyEvaluatePracticeTypeSuggestionQuery } from "store/queries/practiceTypeSuggestion";
import {
  IPracticeTypeSuggestionEvaluateResult,
  PracticeTypeRuleModality,
} from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";

import {
  MODALITY_LABELS,
  SERVICE_TYPE_CHOICES,
  suggestionInWords,
} from "./serviceTypeRuleWording";

const listOf = (data: any): any[] => (data as any)?.data || data || [];

interface IServiceTypeRuleTesterProps {
  visible: boolean;
  onHide: () => void;
}

/**
 * CLE-2971 — "Test an estimate", a dialog opened from the tab's button row: runs the SAVED rules that
 * apply at the chosen hospital (its own plus the all-hospitals ones it does not replace) through the
 * evaluate endpoint — the same loader and evaluator as a real eligibility check. Nothing is sent to
 * pVerify and nothing is stored. What was typed stays while the tab is open.
 */
export function ServiceTypeRuleTester({
  visible,
  onHide,
}: IServiceTypeRuleTesterProps) {
  const [accountId, setAccountId] = useState<string | null>(null);
  const [payerId, setPayerId] = useState<number | null>(null);
  const [planName, setPlanName] = useState("");
  const [practiceTypeCode, setPracticeTypeCode] = useState<string | null>(null);
  const [procedureCodes, setProcedureCodes] = useState<string[]>([]);
  const [result, setResult] =
    useState<IPracticeTypeSuggestionEvaluateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: accountsData } = useGetAllAccountsQuery(null, {
    skip: !visible,
  });
  const { data: payersData } = useGetPayersByAccountQuery(accountId || "", {
    skip: !visible || !accountId,
  });
  const [evaluate, { isFetching }] =
    useLazyEvaluatePracticeTypeSuggestionQuery();

  const accountOptions = sortBy(
    listOf(accountsData).map((account: any) => ({
      label: account.name,
      value: account.id,
    })),
    ["label"]
  );
  const payerOptions = sortBy(
    listOf(payersData).map((payer: any) => ({
      label: payer.name,
      value: payer.id,
    })),
    ["label"]
  );

  const codes = procedureCodes.map((code) => code.trim()).filter(Boolean);
  const ready = !!accountId && !!practiceTypeCode && codes.length > 0;

  const run = async () => {
    if (!accountId || !practiceTypeCode) return;
    setError(null);
    try {
      const response = await evaluate({
        accountId,
        payerId,
        planName: planName || null,
        practiceTypeCode,
        procedureCodes: codes,
      }).unwrap();
      setResult(response);
    } catch (caught: any) {
      setResult(null);
      setError(
        caught?.data?.message || caught?.message || "The test could not run."
      );
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Test an estimate"
      style={{ width: "min(760px, 95vw)" }}
      data-testid="serviceTypeRules.testerDialog"
    >
      {/* The dialog renders through a portal, outside the tab's Surface, so it carries its own. */}
      <Surface data-testid="serviceTypeRules.tester">
        <p className="mt-0 mb-3 text-color-secondary">
          Enter an estimate and the Service Type its eligibility check ran
          under. The result uses the rules as saved, through the same code as a
          real eligibility check.
        </p>

        <div className="grid">
          <div className="col-12 md:col-6 flex flex-column gap-1">
            <Label htmlFor="serviceTypeRules.tester.account">Hospital</Label>
            <Dropdown
              inputId="serviceTypeRules.tester.account"
              options={accountOptions}
              value={accountId}
              onChange={(event) => {
                setAccountId(event.value ?? null);
                setPayerId(null);
              }}
              placeholder="Choose a hospital"
              filter
            />
          </div>
          <div className="col-12 md:col-6 flex flex-column gap-1">
            <Label htmlFor="serviceTypeRules.tester.payer">Payer</Label>
            <Dropdown
              inputId="serviceTypeRules.tester.payer"
              options={payerOptions}
              value={payerId}
              onChange={(event) => setPayerId(event.value ?? null)}
              placeholder="Choose a payer"
              disabled={!accountId}
              showClear
              filter
            />
          </div>
          <div className="col-12 flex flex-column gap-1">
            <Label htmlFor="serviceTypeRules.tester.planName">
              Plan name pVerify returned (optional)
            </Label>
            <InputText
              id="serviceTypeRules.tester.planName"
              data-testid="serviceTypeRules.tester.planName"
              value={planName}
              onChange={(event) => setPlanName(event.target.value)}
            />
          </div>
          <div className="col-12 md:col-6 flex flex-column gap-1">
            <Label htmlFor="serviceTypeRules.tester.practiceType">
              Service Type the check ran under
            </Label>
            <Dropdown
              inputId="serviceTypeRules.tester.practiceType"
              options={SERVICE_TYPE_CHOICES}
              value={practiceTypeCode}
              onChange={(event) => setPracticeTypeCode(event.value ?? null)}
              placeholder="Choose a Service Type"
            />
          </div>
          <div className="col-12 md:col-6 flex flex-column gap-1">
            <Label htmlFor="serviceTypeRules.tester.codes">CPT codes</Label>
            <Chips
              inputId="serviceTypeRules.tester.codes"
              data-testid="serviceTypeRules.tester.codes"
              value={procedureCodes}
              onChange={(event) => setProcedureCodes(event.value ?? [])}
              separator=","
              addOnBlur
              allowDuplicate={false}
            />
          </div>
        </div>

        <Button
          type="button"
          label="Test"
          icon="pi pi-play"
          className="p-button-sm mb-3"
          loading={isFetching}
          disabled={!ready}
          onClick={run}
          data-testid="serviceTypeRules.tester.run"
        />

        {error && <Message severity="error" text={error} />}

        {result && (
          <div
            className="flex flex-column gap-2"
            data-testid="serviceTypeRules.tester.result"
          >
            <p className="m-0 font-semibold">
              {suggestionInWords(result.suggestion)}
            </p>
            <p className="m-0 text-sm text-color-secondary">
              Procedure types found:{" "}
              {result.modalities
                .filter((modality) => modality !== "OTHER")
                .map(
                  (modality) =>
                    MODALITY_LABELS[modality as PracticeTypeRuleModality]
                )
                .join(", ") || "none (no MRI, CT, ultrasound or X-ray code)"}
            </p>
          </div>
        )}
      </Surface>
    </Dialog>
  );
}
